import { computed, signal } from '@angular/core';
import { Serialize, VisualDataSerializer } from './VisualDataSerializer';
import { Visual } from './Visual';
import { VisualType } from './VisualType';
import { isNullOrUndefined } from '../../shared/utils';
import { VisualContext } from './VisualContext';
import { merge as deepMerge } from 'ts-deepmerge';
import { logger } from '../utils/logger';

const LOGGER = logger('VisualStyle');

export type FontSizeKind = 'px' | 'pt' | 'em';
export type FontSizeValue = { kind: FontSizeKind } & ({ size: number } | { value: number });
export namespace FontSize {
  export function value(fontSize: FontSizeValue, defaultValue?: number): number {
    if (!fontSize)
      return defaultValue;

    if (('value' in fontSize)) {
      if (isNullOrUndefined(fontSize.value) || isNaN(Number(fontSize.value)))
        return defaultValue;

      return fontSize.value;
    }
    
    if (('size' in fontSize)) {
      if (isNullOrUndefined(fontSize.size) || isNaN(Number(fontSize.size)))
        return defaultValue;

      return fontSize.size;
    }

    return defaultValue;
  }

  export function toCss(fontSize: FontSizeValue, defaultValue?: number) {
    defaultValue ??= 12;

    if (!fontSize) {
      // Generally 12px is what the default would be in more places
      // Although this is not great design, we leave it to caller to ensure the value defaults otherwise
      fontSize = {
        value: defaultValue,
        kind: 'px',
      };
    }

    let size = FontSize.value(fontSize, defaultValue);
    if (!size) {
      size = defaultValue;
    }

    return `${size}${fontSize.kind ?? 'px'}`;
  }
}

export interface TextStyle {
  readonly color?: string;
  readonly fontSize?: FontSizeValue;
  readonly underline?: boolean;
  readonly bold?: boolean;
  readonly italic?: boolean;
}

export namespace TextStyle {
  export const Default: TextStyle = {
    color: '#2e2e2e',
    fontSize: null,
    underline: false,
    bold: false,
    italic: false
  };

  export function toCssVariables(text: TextStyle, prefix: string = ''): string {
    let style = [];

    if (text.color)
      style.push(`${prefix}color: ${text.color};`);
    else
      style.push(`${prefix}color: inherit;`);

    if (text.underline)
      style.push(`${prefix}text-decoration: underline;`);
    else
      style.push(`${prefix}text-decoration: inherit;`);

    if (text.fontSize)
      style.push(`${prefix}font-size: ${VisualStyle.fontSizeToCss(text.fontSize)};`);
    else
      style.push(`${prefix}font-size: inherit;`);

    style.push(`${prefix}font-weight: ${text.bold ? 'bold' : 'inherit'};`);
    style.push(`${prefix}font-style: ${text.italic ? 'italic' : 'inherit'};`);

    return style.join('\n');
  }

  export function toHighchartsStyle(text: TextStyle) {
    return text ? {
      color: text.color,
      fontSize: VisualStyle.fontSizeToCss(text.fontSize),
      fontWeight: text.bold ? 'bold' : '400',
      fontStyle: text.italic ? 'italic' : 'normal',
      textDecoration: text.underline ? 'underline' : 'none',
    } : {};
  }
}

export interface TextElementStyle extends TextStyle {
  readonly enabled?: boolean;
}

export namespace TextElementStyle {
  export function SerializationOptions(defaultValue: TextElementStyle) {
    return {
      read: s => ({
        ...defaultValue,
        ...s,
        fontSize: {
          ...defaultValue.fontSize,
          ...s?.fontSize
        },
      }),
      write: s => s,
      defaultValue
    };
  }

  export const serializationOptions = SerializationOptions(TextStyle.Default);

  export function parse(json: string, key: string, defaultValue?: TextElementStyle): TextElementStyle {
    defaultValue ??= { enabled: false };

    if (!json)
      return defaultValue;

    try {
      let style = VisualStyle.parsePart<TextElementStyle>(json, key);
      return {
        ...TextElementStyle.serializationOptions.defaultValue,
        ...defaultValue,
        ...style
      };
    }
    catch (err) {
      return defaultValue;
    }
  }
}

export class VisualStyle {
  @Serialize({ defaultValue: '#ffffff' })
  readonly backgroundColor = signal<string>(null);

  @Serialize(TextElementStyle.SerializationOptions({ ...TextStyle.Default, enabled: true }))
  readonly title = signal<TextElementStyle>(null);

  @Serialize(TextElementStyle.SerializationOptions({ ...TextStyle.Default, enabled: false }))
  readonly subtitle = signal<TextElementStyle>(null);
  
  /**
   * Serializes/deserializes the style.
   **/
  serialize(s: VisualDataSerializer): void {
    if (!s.deserializing)
      this.beforeSerialized(s.context);

    s.serializeAnnotated(this);

    if (s.deserializing)
      this.afterDeserialized(s.context);
  }
  
  serializeToObject(context: VisualContext): any {
    this.beforeSerialized(context);
    let obj = {};
    let s = new VisualDataSerializer(context, obj, false);
    this.serialize(s);
    return <any>obj;
  }

  protected beforeSerialized(context: VisualContext) { }


  deserializeFromObject(obj: any, context: VisualContext, merge?: boolean) {
    if (merge) {
      obj = deepMerge(obj, this.serializeToObject(context));
    }

    let s = new VisualDataSerializer(context, obj, true);
    this.serialize(s);
    this.afterDeserialized(context);
  }

  protected afterDeserialized(context: VisualContext) { }


  toCss(): string {
    let builder: string[] = [];
    this.buildCss(builder);
    return builder.join('\n');
  }

  private readonly baseStyleCss = computed(() => {
    let css: string[] = [];

    css.push(
      `${VisualStyle.BackgroundColorCssVariable}: ${this.backgroundColor()};`);


    css.push(
      `${VisualStyle.TitleCssVariablePrefix}display: ${this.title()?.enabled ? 'block' : 'none'};`);

    css.push(
      TextStyle.toCssVariables(this.title(), VisualStyle.TitleCssVariablePrefix));


    css.push(
      `${VisualStyle.SubtitleCssVariablePrefix}display: ${this.subtitle()?.enabled ? 'block' : 'none'};`);

    css.push(
      TextStyle.toCssVariables(this.subtitle(), VisualStyle.SubtitleCssVariablePrefix));

    return css;
  });

  protected buildCss(builder: string[]) {
    builder.push(...this.baseStyleCss());
  }
}

export namespace VisualStyle {
  export const BackgroundColorCssVariable = `--pbir-visual__background-color`;
  export const TitleCssVariablePrefix = '--pbir-visual__title-';
  export const SubtitleCssVariablePrefix = '--pbir-visual__subtitle-';

  export function parsePart<T>(json: string, key: string, defaultValue?: T): T {
    try {
      let style = JSON.parse(json);
      if (!(key in style)) {
        LOGGER.debug(`'${key}' not found json`, json);
        return defaultValue;
      }

      return JSON.parse(style[key]) ?? defaultValue;
    }
    catch (err) {
      LOGGER.warn(`Failed to parse '${key}' from style json`, err, json);
      return defaultValue;
    }
  }

  export function fontSizeToCss(fontSize: FontSizeValue) {
    return FontSize.toCss(fontSize);
  }
}

